/**
 * Print-only white card atom for Monthly Consultation PDF.
 */

import React from "react";
import {
  border,
  cardPadding,
  cardRadius,
  cardShadow,
  cardWhite,
} from "../pdfTheme";

/**
 * Props for a white content card with optional left accent border.
 */
export interface PdfCardProps {
  /** Card body content. */
  children: React.ReactNode;
  /** Optional 4px left-border accent color (theme token). */
  accentColor?: string;
  /** Optional extra CSS class names appended after `mc-pdf-card`. */
  className?: string;
  /**
   * Stretch to the parent grid/flex cell height and stack children as a column.
   * Used when a chapter fills leftover A4 space by growing card rows.
   */
  fillHeight?: boolean;
}

/**
 * White rounded card with thin border and soft shadow.
 * Applies `.mc-pdf-card` for print break-inside avoidance.
 */
export const PdfCard: React.FC<PdfCardProps> = ({
  children,
  accentColor,
  className,
  fillHeight = false,
}) => {
  const extraClass =
    typeof className === "string" && className.trim().length > 0
      ? className.trim()
      : "";
  const classNames = ["mc-pdf-card", extraClass].filter((c) => c.length > 0).join(" ");

  const hasAccent =
    typeof accentColor === "string" && accentColor.trim().length > 0;
  const shouldFill = fillHeight === true;

  return (
    <div
      className={classNames}
      style={{
        backgroundColor: cardWhite,
        border: `1px solid ${border}`,
        borderRadius: cardRadius,
        boxShadow: cardShadow,
        padding: cardPadding,
        boxSizing: "border-box",
        ...(shouldFill
          ? {
              height: "100%",
              display: "flex",
              flexDirection: "column" as const,
              minHeight: 0,
            }
          : {}),
        ...(hasAccent
          ? {
              borderLeftWidth: 4,
              borderLeftStyle: "solid" as const,
              borderLeftColor: accentColor,
            }
          : {}),
      }}
    >
      {children}
    </div>
  );
};
